import React, { useState, useEffect, useMemo } from "react";
import { getFresh } from "../common/apiCache";
import {
    XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend,
    ResponsiveContainer, ReferenceLine, LineChart, Line,
} from "recharts";
import { SlidersHorizontal, TrendingUp, RotateCcw, MousePointerClick, ChevronLeft, ChevronRight } from "lucide-react";
import Loader from "../common/Loader";
import MultiSelectDropdown from "../common/MultiSelectDropdown";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL

// ── App palette ─────────────────────────────────────────────────────────────
const PALETTE = [
    "#032B4E", "#2EB8AA", "#F2994A", "#7a35c8", "#e74c3c",
    "#3498db", "#27ae60", "#f39c12", "#9b59b6", "#1abc9c",
    "#e67e22", "#34495e", "#2ecc71", "#8e44ad", "#2980b9",
];

// ── Trend-tab filters ─────────────────────────────────────────────────────────
// Display order in the sidebar (top to bottom).
const FILTER_CONFIG = [
    { key: "dataset", label: "Product Type", mode: "single" },
    { key: "region_level", label: "Geographic Granularity", mode: "single" },
    { key: "financial_year", label: "Financial Year", mode: "multi" },
    { key: "zone", label: "Zone", mode: "single", hideWhenNational: true },
    { key: "segment", label: "Segment", mode: "single" },
    { key: "product_category", label: "Product Category", mode: "single" },
    { key: "feature", label: "Feature", mode: "multi" },
];
const FILTER_KEYS = FILTER_CONFIG.map((f) => f.key);

// Cascade (narrowing) order — deliberately different from the display order
// above. Product Type's available options must be computed from rows already
// narrowed by Geographic Granularity, so Geographic Granularity has to be
// processed first internally even though it's shown second on screen.
const CASCADE_ORDER = ["region_level", "dataset", "financial_year", "zone", "segment", "product_category", "feature"];
const CASCADE_CONFIG = CASCADE_ORDER.map((key) => FILTER_CONFIG.find((f) => f.key === key));
const MAX_SERIES = 15;
const INVALID_LEVELS = new Set(["0", "nan", "none", "(blank)"]);

// ── Formatting: exact numbers, truncated (never rounded) to 2 decimals ──────
const truncateDp = (v, dp = 2) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    const factor = 10 ** dp;
    return Math.trunc(n * factor) / factor;
};
const fmtExact = (v) => {
    const t = truncateDp(v, 2);
    return t === null ? "-" : t.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtCompact = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(2) + "M";
    if (Math.abs(n) >= 1000) return (n / 1000).toFixed(2) + "K";
    return (truncateDp(n, 2) ?? 0).toFixed(2);
};

const yAxisLabelProps = (value) => ({
    value,
    angle: -90,
    position: "center",
    style: { fontSize: 12, fill: "#64748b", textAnchor: "middle" },
});

const norm = (v) => (v === null || v === undefined || String(v).trim() === "" ? "(Blank)" : String(v));

// ── Shared style tokens ─────────────────────────────────────────────────────
// Every header (section titles, chart titles, the "Dynamic Filters" sidebar
// title) shares one size/style, and every filter label shares another —
// identical between Trend Analysis and Conjoint / New Product Development.
const ST = {
    bigTitle: { fontSize: 18, fontWeight: 700, color: "#0c3c54", fontFamily: "Poppins, sans-serif" },
    subtitle: { fontSize: 14, color: "#6a7d8c", fontFamily: "Poppins, sans-serif" },
    card: {
        background: "#fff", border: "1px solid #e6eef4", borderRadius: 12,
        boxShadow: "0 3px 12px rgba(12, 60, 84, 0.06)",
    },
    filterLabel: {
        display: "block", fontSize: 13, fontWeight: 600, color: "#0c3c54",
        fontFamily: "Poppins, sans-serif", marginBottom: 6, marginTop: 14,
    },
    center: {
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "60px 20px", textAlign: "center", minHeight: 300,
    },
};

// ── Generic chart tooltip (shows exact values for every series at the point) ─
function ChartTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    const shown = payload.filter((p) => p.value !== null && p.value !== undefined);
    if (!shown.length) return null;
    return (
        <div style={{ background: "#fff", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", minWidth: 190, maxWidth: 320 }}>
            <strong style={{ color: "#1e293b", marginBottom: 6, display: "block" }}>{label}</strong>
            {shown.map((p) => (
                <div key={p.dataKey} style={{ color: "#374151", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: p.color || p.stroke, flexShrink: 0 }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}:</span>
                    <strong style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>{fmtExact(p.value)}</strong>
                </div>
            ))}
        </div>
    );
}

// ── Build a { year, [seriesValue]: mean(metric) } grid from raw rows ─────────
// `seriesKey` is the column that becomes the lines (feature or feature_level);
// `valueKey` is the numeric column plotted on the Y-axis. Duplicate rows for the
// same (year, series) are averaged.
function buildSeriesGrid(rows, seriesKey, valueKey, { dropInvalidLevels = false } = {}) {
    const years = new Set();
    const seriesSet = new Set();
    const acc = new Map(); // `${year}␟${series}` -> { sum, count }

    rows.forEach((r) => {
        const year = norm(r.financial_year);
        const s = norm(r[seriesKey]);
        if (dropInvalidLevels && INVALID_LEVELS.has(s.toLowerCase())) return;
        const v = Number(r[valueKey]);
        if (!Number.isFinite(v)) return;

        years.add(year);
        seriesSet.add(s);
        const k = year + "␟" + s;
        const e = acc.get(k) || { sum: 0, count: 0 };
        e.sum += v;
        e.count += 1;
        acc.set(k, e);
    });

    const seriesList = [...seriesSet].sort();
    const data = [...years].sort((a, b) => String(a).localeCompare(String(b))).map((year) => {
        const row = { year };
        seriesList.forEach((s) => {
            const e = acc.get(year + "␟" + s);
            row[s] = e ? e.sum / e.count : null;
        });
        return row;
    });

    return { data, series: seriesList.slice(0, MAX_SERIES), truncated: seriesList.length > MAX_SERIES };
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function TrendAnalysisDashboard({ embedded = false } = {}) {
    // Raw tables — each fetched once, all filtering/charting runs client-side.
    const [trendRows, setTrendRows] = useState([]);      // trend_analysis  (drill-down A)
    const [impRows, setImpRows] = useState([]);          // conjoint_importance (master)
    const [pwRows, setPwRows] = useState([]);            // conjoint_part_worth (drill-down B)
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    // Filter selections: single → string | undefined; multi → string[] | null (null = ALL)
    const [sel, setSel] = useState({});

    // Master-chart drill-down selection
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(true);

    // ── One-time data fetch (three tables in parallel) ─────────────────────────
    useEffect(() => {
        let active = true;
        setLoadError(null);
        setLoading(true);

        (async () => {
            try {
                const [trendRes, impRes, pwRes] = await Promise.all([
                    getFresh(`${REACT_APP_API_BASE_URL}/trend/data`),
                    getFresh(`${REACT_APP_API_BASE_URL}/trend/conjoint-importance`),
                    getFresh(`${REACT_APP_API_BASE_URL}/trend/conjoint-part-worth`),
                ]);
                if (!active) return;

                if (impRes.data?.status === "success") {
                    setTrendRows(trendRes.data?.data || []);
                    setImpRows(impRes.data?.data || []);
                    setPwRows(pwRes.data?.data || []);
                } else {
                    setLoadError(impRes.data?.message || "No data returned from server.");
                }
            } catch (e) {
                if (active) setLoadError("Failed to load trend data. Please check the server and try again.");
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [reloadKey]);

    // ── Cascading filters (driven by the conjoint_importance / master table) ──
    const cascade = useMemo(() => {
        let rows = impRows;
        const per = {};
        const eff = {}; // key -> allowed values (string[]) or null (no filter)
        let regionIsNational = false;

        for (const cfg of CASCADE_CONFIG) {
            const { key, mode, hideWhenNational } = cfg;
            const hidden = hideWhenNational && regionIsNational;

            if (hidden) {
                per[key] = { options: [], value: null, effective: [], hidden: true };
                eff[key] = null; // National → ignore Zone entirely
                continue;
            }

            const options = [...new Set(rows.map((r) => norm(r[key])))].sort();

            if (mode === "single") {
                // "" is an explicit sentinel meaning the user cleared this filter (via Reset
                // Filters or the field's own Clear) — it must stay empty, not silently
                // snap back to a default. `undefined` (never touched) still auto-defaults
                // to the first option, and a stale value no longer in `options` also falls
                // back to the first option rather than being left orphaned.
                const raw = sel[key];
                const value =
                    raw === "" ? null
                        : typeof raw === "string" && options.includes(raw) ? raw
                            : (options[0] ?? null);
                per[key] = { options, value, hidden: false };
                eff[key] = value != null ? [value] : null;
                if (value != null && options.length) {
                    rows = rows.filter((r) => norm(r[key]) === value);
                }
                if (key === "region_level") {
                    regionIsNational = String(value ?? "").toLowerCase() === "national";
                }
            } else {
                const stored = Array.isArray(sel[key]) ? sel[key] : null; // null = all
                const effective = stored ? stored.filter((v) => options.includes(v)) : options;
                per[key] = { options, effective, hidden: false };
                eff[key] = stored ? effective : null;
                if (stored && effective.length !== options.length) {
                    const s = new Set(effective);
                    rows = rows.filter((r) => s.has(norm(r[key])));
                }
            }
        }
        return { per, eff, regionIsNational };
    }, [impRows, sel]);

    const { per, eff } = cascade;

    // Apply the effective filter selection to any of the three tables.
    const filterRows = useMemo(() => (rows, effSel) =>
        rows.filter((r) =>
            FILTER_KEYS.every((key) => {
                const arr = effSel[key];
                if (arr == null) return true;       // no filter (all)
                if (arr.length === 0) return false; // explicitly cleared → exclude
                return arr.includes(norm(r[key]));
            })
        ), []);

    const handleSingleChange = (key, arr) => {
        // "" (not null/undefined) marks an explicit clear so the cascade doesn't
        // immediately re-default it back to the first option.
        setSel((prev) => ({ ...prev, [key]: arr[0] ?? "" }));
    };
    const handleMultiChange = (key, arr) => {
        const opts = per[key]?.options || [];
        setSel((prev) => ({ ...prev, [key]: arr.length === opts.length ? null : arr }));
    };
    // Explicitly clears every single-select filter (their "" sentinel keeps them
    // blank instead of re-defaulting); most multi-selects fall back to "all"
    // simply because they're absent from the cleared object. Financial Year is
    // the one exception — on reset it goes to "none selected", not "all".
    const resetFilters = () => {
        const cleared = { financial_year: [] };
        FILTER_CONFIG.forEach((cfg) => {
            if (cfg.mode === "single") cleared[cfg.key] = "";
        });
        setSel(cleared);
        setSelectedFeature(null);
    };

    // ── Master chart: Feature Importance Over Time ────────────────────────────
    const master = useMemo(() => {
        const rows = filterRows(impRows, eff);
        return buildSeriesGrid(rows, "feature", "relative_importance_pct");
    }, [impRows, eff, filterRows]);

    // Keep the drill-down selection valid as the available features change.
    useEffect(() => {
        if (selectedFeature && !master.series.includes(selectedFeature)) {
            setSelectedFeature(null);
        }
    }, [master.series, selectedFeature]);

    const handleSelectFeature = (feature) => {
        setSelectedFeature((cur) => (cur === feature ? null : feature));
    };

    // ── Drill-down A: Quantity % by Feature Level (trend_analysis) ────────────
    const drillA = useMemo(() => {
        if (!selectedFeature) return { data: [], series: [] };
        const rows = filterRows(trendRows, { ...eff, feature: [selectedFeature] });
        return buildSeriesGrid(rows, "feature_level", "qty_percentage", { dropInvalidLevels: true });
    }, [trendRows, eff, selectedFeature, filterRows]);

    // ── Drill-down B: Part Worth by Feature Level (conjoint_part_worth) ───────
    const drillB = useMemo(() => {
        if (!selectedFeature) return { data: [], series: [] };
        const rows = filterRows(pwRows, { ...eff, feature: [selectedFeature] });
        return buildSeriesGrid(rows, "feature_level", "part_worth", { dropInvalidLevels: true });
    }, [pwRows, eff, selectedFeature, filterRows]);

    // ── KPI values (from trend_analysis) ──────────────────────────────────────
    const trendFiltered = useMemo(() => filterRows(trendRows, eff), [trendRows, eff, filterRows]);
    const kpi = useMemo(() => {
        let sales = 0, qty = 0;
        trendFiltered.forEach((r) => {
            sales += Number(r.net_sales) || 0;
            qty += Number(r.invoiced_quantity) || 0;
        });
        return { sales, qty };
    }, [trendFiltered]);

    // ── Render ────────────────────────────────────────────────────────────────
    const outerStyle = embedded
        ? { width: "100%" }
        : {
            width: "100%", display: "flex", justifyContent: "center", alignItems: "flex-start",
            background: "linear-gradient(135deg, #021a30 0%, #032B4E 30%, #032B4E 70%, #021a30 100%)",
            padding: "40px 0", minHeight: "100vh", boxSizing: "border-box",
            fontFamily: "Inter, sans-serif",
        };
    const cardStyle = embedded
        ? { width: "100%", display: "flex", flexDirection: "column", gap: 12 }
        : {
            width: "98%", maxWidth: 1600, margin: "0 auto", background: "#fff",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)", border: "1px solid #475C7E",
            borderRadius: 8, overflow: "visible", display: "flex", flexDirection: "column",
        };

    const filterSidebar = (
        <aside
            style={{
                ...(embedded ? ST.card : {}),
                width: embedded ? 280 : 300,
                flexShrink: 0,
                alignSelf: embedded ? "stretch" : undefined,
                display: embedded ? "flex" : "block",
                flexDirection: embedded ? "column" : undefined,
                borderLeft: embedded ? undefined : "none",
                borderRight: embedded ? undefined : "1px solid #e5e7eb",
                background: embedded ? "#fff" : "#fafbfc",
                padding: embedded ? "14px 16px 18px" : "20px 20px 28px",
                boxSizing: "border-box",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <SlidersHorizontal size={15} color="#032B4E" />
                <span style={{ ...ST.bigTitle, flex: 1 }}>Dynamic Filters</span>
                {embedded && (
                    <button
                        type="button"
                        onClick={() => setFiltersOpen(false)}
                        title="Hide filters"
                        style={{
                            border: "1px solid #e6eef4",
                            background: "#fff",
                            borderRadius: 6,
                            width: 28,
                            height: 28,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "#0c3c54",
                            flexShrink: 0,
                        }}
                    >
                        <ChevronRight size={14} />
                    </button>
                )}
            </div>

            <button
                onClick={resetFilters}
                title="Reset all filters to defaults"
                style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    background: "#fff", border: "1px solid #E06639", borderRadius: 8,
                    padding: "8px 0", fontSize: 12, fontWeight: 600, color: "#E06639",
                    cursor: "pointer", marginBottom: 4, fontFamily: "Poppins, sans-serif",
                }}
            >
                <RotateCcw size={13} /> Reset Filters
            </button>

            {FILTER_CONFIG.map((cfg) => {
                const info = per[cfg.key];
                if (!info || info.hidden) return null;
                return (
                    <React.Fragment key={cfg.key}>
                        <label style={ST.filterLabel}>{cfg.label}</label>
                        {cfg.mode === "single" ? (
                            <MultiSelectDropdown
                                singleSelect
                                options={info.options}
                                value={info.value != null ? [info.value] : []}
                                onChange={(v) => handleSingleChange(cfg.key, v)}
                                placeholder="Select…"
                            />
                        ) : (
                            <MultiSelectDropdown
                                options={info.options}
                                value={info.effective}
                                onChange={(v) => handleMultiChange(cfg.key, v)}
                            />
                        )}
                    </React.Fragment>
                );
            })}

            <div style={{ ...ST.card, marginTop: 24, padding: "12px 14px", borderTop: "3px solid #E06639" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#6a7d8c", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    Trend Rows
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#0c3c54", fontFamily: "Poppins, sans-serif", marginTop: 4 }}>
                    {trendFiltered.length.toLocaleString("en-US")}
                </div>
                <div style={{ fontSize: 12, color: "#6a7d8c", marginTop: 2 }}>
                    of {trendRows.length.toLocaleString("en-US")} total
                </div>
            </div>
        </aside>
    );

    return (
        <div style={outerStyle}>
            <div style={cardStyle}>
                {!embedded && (
                    <div style={{
                        background: "linear-gradient(135deg, #021a30 0%, #032B4E 50%, #021a30 100%)",
                        padding: "20px 24px",
                    }}>
                        <h2 style={{ color: "#fff", fontSize: 20, fontWeight: 700, margin: 0, fontFamily: "Poppins, sans-serif" }}>
                            Trend Analysis
                        </h2>
                        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, margin: "4px 0 0" }}>
                            Feature importance over time, with feature-level drill-downs
                        </p>
                    </div>
                )}

                {loading ? (
                    <div style={{ ...ST.center, minHeight: 420 }}>
                        <Loader size="large" />
                        <p style={{ marginTop: 16, fontSize: 14, fontWeight: 600, color: "#032B4E" }}>
                            Loading trend data…
                        </p>
                    </div>
                ) : loadError ? (
                    <div style={{ ...ST.center, minHeight: 420 }}>
                        <div style={{ fontSize: 44 }}>⚠️</div>
                        <p style={{ fontSize: 16, fontWeight: 700, color: "#b91c1c", marginTop: 8 }}>Unable to load data</p>
                        <p style={{ fontSize: 14, color: "#6B7280", marginTop: 6 }}>{loadError}</p>
                        <button
                            onClick={() => setReloadKey((k) => k + 1)}
                            style={{
                                marginTop: 16, background: "#f79658", color: "#fff", border: "none",
                                borderRadius: 6, padding: "9px 22px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                            }}
                        >
                            Retry
                        </button>
                    </div>
                ) : (
                    <div style={{ display: "flex", alignItems: "stretch", gap: embedded ? 12 : 0 }}>
                        {(!embedded || (embedded && filtersOpen)) && filterSidebar}
                        
                        {embedded && !filtersOpen && (
                            <button
                                type="button"
                                onClick={() => setFiltersOpen(true)}
                                title="Show filters"
                                style={{
                                    alignSelf: "flex-start",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    border: "1px solid #e6eef4",
                                    background: "#fff",
                                    borderRadius: 8,
                                    padding: "8px 12px",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#0c3c54",
                                    cursor: "pointer",
                                    fontFamily: "Poppins, sans-serif",
                                    boxShadow: "0 2px 8px rgba(12, 60, 84, 0.06)",
                                    flexShrink: 0,
                                }}
                            >
                                <ChevronRight size={14} />
                                Filters
                            </button>
                        )}

                        <div style={{
                            flex: 1,
                            minWidth: 0,
                            padding: embedded ? 0 : "20px 24px 48px",
                            background: embedded ? "transparent" : "#fff",
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                        }}>
                            {/* ── KPI cards ── */}
                            {/* <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 16, marginBottom: 24 }}>
                                {[
                                    { label: "Total Net Sales", value: fmtCompact(kpi.sales), title: fmtExact(kpi.sales), accent: "#032B4E" },
                                    { label: "Total Quantity", value: fmtCompact(kpi.qty), title: fmtExact(kpi.qty), accent: "#2EB8AA" },
                                    { label: "Features", value: String(master.series.length), title: "", accent: "#F2994A" },
                                    { label: "Trend Rows", value: trendFiltered.length.toLocaleString("en-US"), title: "", accent: "#7a35c8" },
                                ].map(({ label, value, title, accent }) => (
                                    <div key={label} title={title} style={{ ...ST.card, padding: "18px 20px", borderTop: `4px solid ${accent}` }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                            {label}
                                        </div>
                                        <div style={{
                                            fontSize: typeof value === "string" && value.length > 14 ? 18 : 26,
                                            fontWeight: 800, color: "#032B4E", marginTop: 6,
                                            fontFamily: "Poppins, sans-serif", overflow: "hidden",
                                            textOverflow: "ellipsis", whiteSpace: "nowrap",
                                        }}>
                                            {value}
                                        </div>
                                    </div>
                                ))}
                            </div> */}

                            {/* ── Master chart: Feature Importance Over Time ── */}
                            <div style={{
                                ...ST.card,
                                padding: "18px 20px",
                                flex: selectedFeature ? "0 0 auto" : 1,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: selectedFeature ? undefined : 520,
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <TrendingUp size={18} color="#032B4E" />
                                    <div style={ST.bigTitle}>Feature Importance Over Time</div>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, ...ST.subtitle, margin: "2px 0 14px" }}>
                                    <MousePointerClick size={14} color="#f79658" />
                                    Click a feature line or its legend to drill down into its feature levels.
                                </div>

                                <div style={{ flex: 1, minHeight: selectedFeature ? 440 : 480 }}>
                                    {master.data.length === 0 || master.series.length === 0 ? (
                                        <div style={{ ...ST.center, minHeight: 200 }}>
                                            <span style={{ color: "#94a3b8", fontSize: 13 }}>No importance data for this selection.</span>
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={master.data} margin={{ top: 12, right: 24, left: 8, bottom: 24 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                                <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#d1d5db" }} />
                                                <YAxis
                                                    width={56}
                                                    tick={{ fontSize: 12, fill: "#64748b" }}
                                                    tickLine={false}
                                                    axisLine={{ stroke: "#d1d5db" }}
                                                    tickFormatter={fmtCompact}
                                                    label={yAxisLabelProps("Relative Importance %")}
                                                />
                                                <ReTooltip content={<ChartTooltip />} />
                                                <Legend
                                                    iconType="circle" iconSize={10}
                                                    onClick={(o) => handleSelectFeature(o.value)}
                                                    formatter={(v) => (
                                                        <span style={{
                                                            fontSize: 12, cursor: "pointer",
                                                            color: selectedFeature === v ? "#032B4E" : "#374151",
                                                            fontWeight: selectedFeature === v ? 800 : 500,
                                                            textDecoration: selectedFeature === v ? "underline" : "none",
                                                        }}>{v}</span>
                                                    )}
                                                />
                                                {master.series.map((f, i) => {
                                                    const dim = selectedFeature && selectedFeature !== f;
                                                    return (
                                                        <Line
                                                            key={f} type="monotone" dataKey={f} name={f}
                                                            stroke={PALETTE[i % PALETTE.length]}
                                                            strokeWidth={selectedFeature === f ? 4 : 2.5}
                                                            strokeOpacity={dim ? 0.25 : 1}
                                                            dot={{ r: 4, cursor: "pointer" }}
                                                            activeDot={{ r: 7, cursor: "pointer", onClick: () => handleSelectFeature(f) }}
                                                            isAnimationActive={false} connectNulls
                                                            onClick={() => handleSelectFeature(f)}
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                    );
                                                })}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                                {master.truncated && (
                                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                                        Showing top {MAX_SERIES} features — narrow the filters to see the rest.
                                    </div>
                                )}
                            </div>

                            {/* ── Drill-downs (hidden until a feature is chosen) ── */}
                            {!selectedFeature ? (
                                <div style={{
                                    ...ST.card,
                                    padding: "26px 20px",
                                    textAlign: "center",
                                    color: "#94a3b8",
                                    background: "#fbfcfe",
                                    border: "1px dashed #cbd5e1",
                                    flexShrink: 0,
                                    marginTop: "auto",
                                }}>
                                    <MousePointerClick size={22} color="#cbd5e1" style={{ marginBottom: 6 }} />
                                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                                        Select a feature in the chart above to see Quantity % and Part Worth by feature level.
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div style={{
                                        display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
                                        padding: "10px 16px", background: "#f0f7ff", border: "1px solid #cfe0ec",
                                        borderRadius: 8,
                                    }}>
                                        <span style={{ fontSize: 13, color: "#374151" }}>
                                            Drilling into feature:&nbsp;<strong style={{ color: "#032B4E" }}>{selectedFeature}</strong>
                                        </span>
                                        <button
                                            onClick={() => setSelectedFeature(null)}
                                            style={{
                                                marginLeft: "auto", background: "#fff", border: "1px solid #d1d5db",
                                                borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 700,
                                                color: "#5E6775", cursor: "pointer",
                                            }}
                                        >
                                            Clear
                                        </button>
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 20, marginBottom: 24 }}>
                                        <DrillChart
                                            title="Quantity Percentage by Feature Level"
                                            yLabel="Quantity %"
                                            data={drillA.data}
                                            series={drillA.series}
                                            emptyText="No quantity data for this feature and selection."
                                        />

                                        <DrillChart
                                            title="Part Worth by Feature Level"
                                            yLabel="Part Worth"
                                            data={drillB.data}
                                            series={drillB.series}
                                            showZeroLine
                                            emptyText="No part worth data for this feature and selection."
                                        />
                                    </div>
                                </>
                            )}
                        </div>


                    </div>
                )}
            </div>
        </div>
    );
}

// ── Reusable drill-down line chart ───────────────────────────────────────────
function DrillChart({ title, yLabel, data, series, showZeroLine = false, emptyText }) {
    // Each chart instance owns its own click-to-highlight selection — clicking a
    // line or its legend entry emphasizes that line and dims the rest so it can
    // be reviewed on its own, independently between the two drill-down charts.
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (selected && !series.includes(selected)) setSelected(null);
    }, [series, selected]);

    const handleSelect = (s) => setSelected((cur) => (cur === s ? null : s));

    return (
        <div style={{ ...ST.card, padding: "18px 20px" }}>
            <div style={ST.bigTitle}>{title}</div>
            <div style={{ height: 400, marginTop: 12 }}>
                {data.length === 0 || series.length === 0 ? (
                    <div style={{ ...ST.center, minHeight: 200 }}>
                        <span style={{ color: "#94a3b8", fontSize: 13 }}>{emptyText}</span>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 12, right: 24, left: 8, bottom: 24 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                            <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#d1d5db" }} />
                            <YAxis
                                width={56}
                                tick={{ fontSize: 12, fill: "#64748b" }}
                                tickLine={false}
                                axisLine={{ stroke: "#d1d5db" }}
                                tickFormatter={fmtCompact}
                                label={yAxisLabelProps(yLabel)}
                            />
                            {showZeroLine && <ReferenceLine y={0} stroke="#94a3b8" />}
                            <ReTooltip content={<ChartTooltip />} />
                            <Legend
                                iconType="circle" iconSize={10}
                                onClick={(o) => handleSelect(o.value)}
                                formatter={(v) => (
                                    <span style={{
                                        fontSize: 12, cursor: "pointer",
                                        color: selected === v ? "#032B4E" : "#374151",
                                        fontWeight: selected === v ? 800 : 500,
                                        textDecoration: selected === v ? "underline" : "none",
                                    }}>{v}</span>
                                )}
                            />
                            {series.map((s, i) => {
                                const dim = selected && selected !== s;
                                return (
                                    <Line
                                        key={s} type="monotone" dataKey={s} name={s}
                                        stroke={PALETTE[i % PALETTE.length]}
                                        strokeWidth={selected === s ? 4 : 2.5}
                                        strokeOpacity={dim ? 0.25 : 1}
                                        dot={{ r: 4, cursor: "pointer" }}
                                        activeDot={{ r: 6, cursor: "pointer", onClick: () => handleSelect(s) }}
                                        isAnimationActive={false} connectNulls
                                        onClick={() => handleSelect(s)}
                                        style={{ cursor: "pointer" }}
                                    />
                                );
                            })}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}
